
// | This module defines helper functions for creating virtual DOM elements
// | safely.
// |
// | __*Note:* DOM element props are provided as records, and checked using `Union`
// |   constraints. This means that we don't need to provide all props, but any we
// |   do provide must have the correct types.__
"use strict";
var $foreign = require("./foreign.js");
var Control_Applicative = require("../Control.Applicative/index.js");
var Control_Bind = require("../Control.Bind/index.js");
var Data_Either = require("../Data.Either/index.js");
var Data_Function_Uncurried = require("../Data.Function.Uncurried/index.js");
var Data_Maybe = require("../Data.Maybe/index.js");
var Data_Nullable = require("../Data.Nullable/index.js");
var Data_Unit = require("../Data.Unit/index.js");
var Effect = require("../Effect/index.js");
var Effect_Exception = require("../Effect.Exception/index.js");
var Effect_Uncurried = require("../Effect.Uncurried/index.js");
var Prelude = require("../Prelude/index.js");
var React_Basic = require("../React.Basic/index.js");
var React_Basic_DOM_Generated = require("../React.Basic.DOM.Generated/index.js");
var React_Basic_DOM_Internal = require("../React.Basic.DOM.Internal/index.js");
var Unsafe_Coerce = require("../Unsafe.Coerce/index.js");
var Web_DOM = require("../Web.DOM/index.js");

// | Attempt to unmount and clean up the React app
// | rendered into the given element. Returns `true`
// | if an app existed and was unmounted successfully.
// |
// | __*Note:* Relies on `ReactDOM.unmountComponentAtNode`__
var unmount = Effect_Uncurried.runEffectFn1($foreign.unmountComponentAtNode_);

// | Create a text node.
var text = Unsafe_Coerce.unsafeCoerce;

// | Render or update/re-render a component tree into
// | a DOM element. The given Effect is run once the
// | DOM update is complete.
// |
// | __*Note:* Relies on `ReactDOM.render`__
var render$prime = Effect_Uncurried.runEffectFn3($foreign.render_);

// | Render or update/re-render a component tree into
// | a DOM element.
// |
// | __*Note:* Relies on `ReactDOM.render`__
var render = function (jsx) {
    return function (node) {
        return render$prime(jsx)(node)(Control_Applicative.pure(Effect.applicativeEffect)(Data_Unit.unit));
    };
};

// | Render or update/re-render a component tree into
// | a DOM element, attempting to reuse the existing
// | DOM tree. The given Effect is run once the
// | DOM update is complete.
// |
// | __*Note:* Relies on `ReactDOM.hydrate`, generally only
// |   used with `ReactDOMServer.renderToNodeStream` or
// |   `ReactDOMServer.renderToString`__
var hydrate$prime = Effect_Uncurried.runEffectFn3($foreign.hydrate_);

// | Render or update/re-render a component tree into
// | a DOM element, attempting to reuse the existing
// | DOM tree.
// |
// | __*Note:* Relies on `ReactDOM.hydrate`, generally only
// |   used with `ReactDOMServer.renderToNodeStream` or
// |   `ReactDOMServer.renderToString`__
var hydrate = function (jsx) {
    return function (node) {
        return hydrate$prime(jsx)(node)(Control_Applicative.pure(Effect.applicativeEffect)(Data_Unit.unit));
    };
};

// | Returns the current DOM node associated with the given
// | instance, or an Error if no node was found or the given
// | instance was not mounted.
// |
// | __*Note:* This function can be *very* slow -- prefer
// | `React.Basic.DOM.Components.Ref` where possible__
// |
// | __*Note:* Relies on `ReactDOM.findDOMNode`__
var findDOMNode = function (instance_) {
    return Effect_Exception["try"](function __do() {
        var v = $foreign.findDOMNode_(instance_);
        var v1 = Data_Nullable.toMaybe(v);
        if (v1 instanceof Data_Maybe.Nothing) {
            return Effect_Exception["throw"]("Node not found.")();
        };
        if (v1 instanceof Data_Maybe.Just) {
            return v1.value0;
        };
        throw new Error("Failed pattern match at React.Basic.DOM (line 99, column 3 - line 101, column 22): " + [ v1.constructor.name ]);
    });
};

// | Create a value of type `CSS` (which can be provided to the `style` property)
// | from a plain record of CSS attributes.
// |
// | For example:
// |
// | ```
// | div { style: css { padding: "5px" } } [ text "This text is padded." ]
// | ```
var css = Unsafe_Coerce.unsafeCoerce;

// | Divert a render tree into a separate DOM node. The node's
// | content will be overwritten and managed by React, similar
// | to `render` and `hydrate`.
var createPortal = Data_Function_Uncurried.runFn2($foreign.createPortal_);
module.exports = {
    render: render,
    "render'": render$prime,
    hydrate: hydrate,
    "hydrate'": hydrate$prime,
    unmount: unmount,
    findDOMNode: findDOMNode,
    createPortal: createPortal,
    text: text,
    css: css,
    mergeStyles: $foreign.mergeStyles
};
